import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const {definePartsStyle, defineMultiStyleConfig} = createMultiStyleConfigHelpers([
  'root',
  'heading',
  'section',
  'sectionHeading',
  'optionsList',
  'itemList',
  'coverItem',
  'optionItem',
  'cushionItem',
  'itemRow',
  'coverItemImage',
  'optionItemImage',
  'cushionItemImage',
  'itemText',
  'primaryName',
  'secondaryLabel',
  'slotLabel',
  'optionItemTitle',
  'optionItemDescription',
  'shelvesSlotLabel',
  'shelvesPrimaryName',
  'notPerfectMatchRoot',
  'notPerfectMatchCard',
  'notPerfectMatchRow',
  'notPerfectMatchContent',
  'notPerfectMatchTitle',
  'notPerfectMatchDescription',
  'notPerfectMatchLinkRow',
  'notPerfectMatchLink',
  'notPerfectMatchImageWrapper',
  'notPerfectMatchImage',
]);

const coverItemBase = {
  alignItems: 'center',
  backgroundColor: 'white',
  borderRadius: '4px',
  boxShadow: 'none',
  cursor: 'default',
  display: 'flex',
  minH: '126px',
  outline: 'none',
  p: '16px',
  pointerEvents: 'none',
  width: '100%',
  _focus: {boxShadow: 'none', outline: 'none'},
  _focusVisible: {boxShadow: 'none', outline: 'none'},
  _hover: {backgroundColor: 'white'},
};

const optionItemBase = {
  alignItems: 'center',
  backgroundColor: 'white',
  borderRadius: '4px',
  boxShadow: 'none',
  cursor: 'default',
  display: 'flex',
  minH: 'auto',
  outline: 'none',
  p: '16px',
  pointerEvents: 'none',
  width: '100%',
  _focus: {boxShadow: 'none', outline: 'none'},
  _focusVisible: {boxShadow: 'none', outline: 'none'},
  _hover: {backgroundColor: 'white'},
};

/** Cover row — 112×88, aspect-ratio 14/11 */
const coverItemImage = {
  aspectRatio: '14/11',
  borderBottomLeftRadius: '6px',
  borderBottomRightRadius: '4px',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '6px',
  flexShrink: 0,
  h: '88px',
  objectFit: 'cover',
  overflow: 'hidden',
  w: '112px',
};

/** Options list (Shelves) — 112×88, aspect-ratio 14/11 */
const optionItemImage = {
  aspectRatio: '14/11',
  borderBottomLeftRadius: '6px',
  borderBottomRightRadius: '4px',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '6px',
  flexShrink: 0,
  h: '88px',
  objectFit: 'cover',
  overflow: 'hidden',
  w: '112px',
};

/** Cushion option rows — same frame as shelves; product image uses contain. */
const cushionItemImage = {
  ...optionItemImage,
  objectFit: 'contain',
};

const baseStyle = definePartsStyle({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    width: '100%',
    marginTop: {base: '24px', lg: '24px'},
    marginBottom: {base: '40px', lg: '48px'},
  },
  heading: {
    color: 'brandColors.greyscale',
    fontFamily: 'body',
    fontSize: {base: '20px', lg: '24px'},
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: {base: 1.5, lg: 1.4},
    margin: 0,
    width: '100%',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  sectionHeading: {
    color: 'brandColors.greyscale',
    fontFamily: 'body',
    fontSize: {base: '16px', lg: '18px'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 500,
    lineHeight: {base: 1.4, lg: 1.2},
    margin: 0,
    width: '100%',
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    width: '100%',
  },
  itemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  coverItem: {
    ...coverItemBase,
    border: {base: '1px solid rgba(27, 26, 24, 0.10)', lg: '2px solid rgba(27, 26, 24, 0.10)'},
    borderRadius: '4px',
  },
  optionItem: {
    ...optionItemBase,
    border: '1px solid #d1d1d1',
    borderRadius: '4px',
  },
  cushionItem: {
    ...optionItemBase,
    border: '1px solid rgba(27, 26, 24, 0.10)',
    borderRadius: '4px',
  },
  itemRow: {
    alignItems: 'center',
    display: 'flex',
    gap: '16px',
    width: '100%',
  },
  coverItemImage,
  optionItemImage,
  cushionItemImage,
  itemText: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'center',
    minWidth: 0,
  },
  primaryName: {
    color: 'grayAlpha.90',
    fontFamily: 'body',
    fontSize: {base: '14px', lg: '14px'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    lineHeight: 1.4,
    margin: 0,
    sx: {
      '&.finished-good-cover-row__material': {
        fontSize: {base: '14px', lg: '16px'},
      },
    },
  },
  secondaryLabel: {
    color: 'greyscale.70',
    fontFamily: 'body',
    fontSize: {base: '13px', lg: '12px'},
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 1.2,
    margin: 0,
  },
  slotLabel: {
    color: 'greyscale.70',
    fontFamily: 'body',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 1.2,
    margin: 0,
  },
  /** Cushion option rows — title (Figma Desktop+Mobile text.label.default). */
  optionItemTitle: {
    color: 'brandColors.greyscale90',
    fontFamily: 'body',
    fontSize: {base: '14px', lg: '16px'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    lineHeight: '140%',
    margin: 0,
  },
  /** Cushion option rows — description (Figma Desktop+Mobile text.body.small). */
  optionItemDescription: {
    color: 'greyscale.60',
    fontFamily: 'body',
    fontSize: {base: '13px', lg: '12px'},
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '120%',
    margin: 0,
  },
  /** Shelves rows — slot label on top (Figma 7730:69145 / 7730:68762). */
  shelvesSlotLabel: {
    color: 'greyscale.60',
    fontFamily: 'body',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '120%',
    margin: 0,
  },
  /** Shelves rows — product name below slot label (Figma 7730:69145 / 7730:68762). */
  shelvesPrimaryName: {
    color: 'brandColors.greyscale90',
    fontFamily: 'body',
    fontSize: '14px',
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    lineHeight: '140%',
    margin: 0,
  },
  /** "Not your perfect match?" CTA — Figma 7730:69224 (desktop) / 7730:68802 (mobile). */
  notPerfectMatchRoot: {
    width: '100%',
  },
  notPerfectMatchCard: {
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    width: '100%',
    _hover: {
      backgroundColor: 'white',
    },
  },
  notPerfectMatchRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
    height: '133px',
    width: '100%',
  },
  notPerfectMatchContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'center',
    minWidth: 0,
    padding: '16px 0 16px 20px',
    maxW: '214px',
  },
  notPerfectMatchTitle: {
    sx: {
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: {base: '14px', lg: '16px'},
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 600,
        lineHeight: 1.4,
        margin: 0,
      },
      '& p': {
        color: 'grayAlpha.70',
        fontFamily: 'body',
        fontSize: {base: '13px', lg: '12px'},
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.2,
        margin: 0,
      },
      '& h1 + p, & h2 + p, & h3 + p, & h4 + p, & h5 + p, & h6 + p': {
        marginTop: '2px',
      },
    },
  },
  notPerfectMatchDescription: {
    margin: 0,
    padding: 0,
    sx: {
      '& .chakra-text': {
        marginTop: '2px',
      },
    },
  },
  notPerfectMatchLinkRow: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
  },
  notPerfectMatchLink: {
    color: 'brandColors.primaryDarkLinen',
    fontFamily: 'body',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 1.2,
    textAlign: 'left',
    textDecoration: 'underline',
    sx: {
      textDecorationSkipInk: 'auto',
      textDecorationThickness: 'auto',
      textUnderlineOffset: 'auto',
      textUnderlinePosition: 'from-font',
    },
  },
  notPerfectMatchImageWrapper: {
    flexShrink: 0,
    height: '133px',
    overflow: 'hidden',
    width: '131px',
  },
  notPerfectMatchImage: {
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
});

/** Finished Good PDP "What's included" list — Figma nodes 6492:12081 / 6492:11698. */
const FinishedGoodView = defineMultiStyleConfig({
  baseStyle,
});

export default FinishedGoodView;
